import { BlackjackEvents } from '../events/blackjack.js';

export function validateBlackjackBetConfirmation(bet: number, roundId: string): void {
  if (typeof bet !== 'number' || bet <= 0 || !isFinite(bet)) {
    throw new Error(`Invalid bet amount: ${bet}. Must be a positive number`);
  }
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
}

export function validateBlackjackHandAction(action: BlackjackEvents.PlayerMove): void {
  if (!BlackjackEvents.PLAYER_MOVES.includes(action)) {
    throw new Error(
      `Invalid action: ${action}. Must be one of: ${BlackjackEvents.PLAYER_MOVES.join(', ')}`
    );
  }
}

export function validateBlackjackHandsUpdate(
  dealerHand: string[],
  playerHandCards: string[],
  playerHandTotal: number,
  insuranceCost?: number
): void {
  if (!Array.isArray(dealerHand) || !Array.isArray(playerHandCards)) {
    throw new Error('dealerHand and playerHandCards must be arrays');
  }
  if (typeof playerHandTotal !== 'number') {
    throw new Error('playerHandTotal must be a number');
  }
  if (insuranceCost !== undefined && (typeof insuranceCost !== 'number' || insuranceCost <= 0)) {
    throw new Error('insuranceCost must be a positive number when provided');
  }
}

export interface BlackjackSplitHandsUpdateInput {
  dealerHand: string[];
  playerHands: BlackjackEvents.PlayerHand[];
  activeHand: 0 | 1;
  dealerHandTotal?: number;
  insuranceCost?: number;
}

export function validateBlackjackSplitHandsUpdate(input: BlackjackSplitHandsUpdateInput): void {
  const { dealerHand, playerHands, activeHand, dealerHandTotal, insuranceCost } = input;
  if (!Array.isArray(dealerHand)) {
    throw new Error('dealerHand must be an array');
  }
  if (!Array.isArray(playerHands) || playerHands.length !== 2) {
    throw new Error('playerHands must be an array of exactly 2 hands');
  }
  if (activeHand !== 0 && activeHand !== 1) {
    throw new Error('activeHand must be 0 or 1');
  }
  for (const [idx, hand] of playerHands.entries()) {
    if (
      !hand ||
      typeof hand !== 'object' ||
      !Array.isArray(hand.cards) ||
      hand.cards.length === 0
    ) {
      throw new Error(`playerHands[${idx}].cards must be a non-empty string[]`);
    }
    if (hand.total !== undefined && (typeof hand.total !== 'number' || !isFinite(hand.total))) {
      throw new Error(`playerHands[${idx}].total must be a finite number if provided`);
    }
  }
  if (
    dealerHandTotal !== undefined &&
    (typeof dealerHandTotal !== 'number' || !isFinite(dealerHandTotal))
  ) {
    throw new Error('dealerHandTotal must be a finite number if provided');
  }
  if (insuranceCost !== undefined && (typeof insuranceCost !== 'number' || insuranceCost <= 0)) {
    throw new Error('insuranceCost must be a positive number when provided');
  }
}

export interface BlackjackGameOverInput {
  dealerHand: string[];
  dealerHandTotal: number;
  playerHandCards: string[];
  playerHandStatus: BlackjackEvents.GameOutcome;
  playerHandTotal: number;
  win: number;
  roundId: string;
  insuranceTaken?: boolean;
  insuranceWin?: number;
}

export function validateBlackjackGameOver(input: BlackjackGameOverInput): void {
  const {
    dealerHand,
    dealerHandTotal,
    playerHandCards,
    playerHandStatus,
    playerHandTotal,
    win,
    roundId,
    insuranceTaken,
    insuranceWin,
  } = input;

  if (!Array.isArray(dealerHand) || !Array.isArray(playerHandCards)) {
    throw new Error('dealerHand and playerHandCards must be arrays');
  }
  if (
    !Number.isFinite(dealerHandTotal) ||
    !Number.isFinite(playerHandTotal) ||
    !Number.isFinite(win)
  ) {
    throw new Error('dealerHandTotal, playerHandTotal, and win must be numbers');
  }
  // Contract: `win` is gross main return (stake + payout). Never negative.
  // See docs/features/blackjack-event-contract.md
  if (win < 0) {
    throw new Error(`win must be >= 0 (gross main return). Got: ${win}`);
  }
  if (!BlackjackEvents.GAME_OUTCOMES.includes(playerHandStatus)) {
    throw new Error(`Invalid playerHandStatus: ${playerHandStatus}`);
  }
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
  if (insuranceTaken !== undefined && typeof insuranceTaken !== 'boolean') {
    throw new Error('insuranceTaken must be boolean if provided');
  }
  if (insuranceWin !== undefined) {
    if (typeof insuranceWin !== 'number' || !isFinite(insuranceWin)) {
      throw new Error('insuranceWin must be a finite number if provided');
    }
    // Contract: gross insurance return. Never negative.
    if (insuranceWin < 0) {
      throw new Error(`insuranceWin must be >= 0 (gross insurance return). Got: ${insuranceWin}`);
    }
    if (insuranceTaken !== true) {
      throw new Error('insuranceWin requires insuranceTaken=true');
    }
  }
}

export interface BlackjackSplitGameOverInput {
  dealerHand: string[];
  dealerHandTotal: number;
  playerHands: BlackjackEvents.PlayerHand[];
  playerHandStatus: BlackjackEvents.RoundOutcome;
  win: number;
  roundId: string;
  insuranceTaken?: boolean;
  insuranceWin?: number;
}

export function validateBlackjackSplitGameOver(input: BlackjackSplitGameOverInput): void {
  const {
    dealerHand,
    dealerHandTotal,
    playerHands,
    playerHandStatus,
    win,
    roundId,
    insuranceTaken,
    insuranceWin,
  } = input;

  if (!Array.isArray(dealerHand)) {
    throw new Error('dealerHand must be an array');
  }
  if (typeof dealerHandTotal !== 'number' || !isFinite(dealerHandTotal)) {
    throw new Error('dealerHandTotal must be a finite number');
  }
  if (!Array.isArray(playerHands) || playerHands.length !== 2) {
    throw new Error('playerHands must be an array of exactly 2 hands');
  }
  if (!BlackjackEvents.ROUND_OUTCOMES.includes(playerHandStatus)) {
    throw new Error(`Invalid playerHandStatus for split game_over: ${playerHandStatus}`);
  }
  if (typeof win !== 'number' || !isFinite(win)) {
    throw new Error('win must be a finite number');
  }
  // Contract: `win` is gross main return summed across both hands. Never negative.
  // See docs/features/blackjack-event-contract.md
  if (win < 0) {
    throw new Error(`win must be >= 0 (gross main return). Got: ${win}`);
  }
  if (typeof roundId !== 'string' || roundId.trim().length === 0) {
    throw new Error('roundId must be a non-empty string');
  }
  for (const [idx, hand] of playerHands.entries()) {
    if (
      !hand ||
      typeof hand !== 'object' ||
      !Array.isArray(hand.cards) ||
      hand.cards.length === 0
    ) {
      throw new Error(`playerHands[${idx}].cards must be a non-empty string[]`);
    }
    if (hand.status !== undefined && typeof hand.status !== 'string') {
      throw new Error(`playerHands[${idx}].status must be a string if provided`);
    }
    if (hand.win !== undefined) {
      if (typeof hand.win !== 'number' || !isFinite(hand.win)) {
        throw new Error(`playerHands[${idx}].win must be a finite number if provided`);
      }
      // Per-hand gross return. Never negative.
      if (hand.win < 0) {
        throw new Error(`playerHands[${idx}].win must be >= 0 (gross per-hand return). Got: ${hand.win}`);
      }
    }
  }
  if (insuranceTaken !== undefined && typeof insuranceTaken !== 'boolean') {
    throw new Error('insuranceTaken must be boolean if provided');
  }
  if (insuranceWin !== undefined) {
    if (typeof insuranceWin !== 'number' || !isFinite(insuranceWin)) {
      throw new Error('insuranceWin must be a finite number if provided');
    }
    if (insuranceWin < 0) {
      throw new Error(`insuranceWin must be >= 0 (gross insurance return). Got: ${insuranceWin}`);
    }
    if (insuranceTaken !== true) {
      throw new Error('insuranceWin requires insuranceTaken=true');
    }
  }
}

export function validateBlackjackInsuranceAction(taken: boolean, amount?: number, roundId?: string): void {
  if (typeof taken !== 'boolean') {
    throw new Error('taken must be a boolean');
  }
  if (taken) {
    if (typeof amount !== 'number' || !isFinite(amount) || amount <= 0) {
      throw new Error(
        `Invalid insurance amount: ${amount}. Must be a positive number when taken=true`
      );
    }
  } else if (amount !== undefined) {
    throw new Error('amount must be omitted when taken=false');
  }
  if (roundId !== undefined && typeof roundId !== 'string') {
    throw new Error('roundId must be a string when provided');
  }
}
